// Tracking — right-pane detail view of a selected order. Shows progress
// stepper, items list, totals, payment + delivery info, and the
// receipt-confirmation + review CTAs.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { supabase } from "../../lib/supabase";
import { gradientForId } from "../../lib/gradients";
import { fmtK } from "../../lib/money";
import {
  STATUS_MAP,
  STEPS,
  TONE,
  DELIVERY_LABEL,
  PAYMENT_LABEL,
  PAYMENT_ICON,
  fmtWhen,
  itemTitle,
  firstItemImage,
} from "./constants";
import { ConfirmReceiptModal, ReviewModal } from "./Modals";

export function Tracking({ sel, user, onMessageSeller }) {
  const queryClient = useQueryClient();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const map = STATUS_MAP[sel.status] || STATUS_MAP.pending_payment;
  const tone = TONE[map.tone];
  const [g1, g2] = gradientForId(sel.id);
  const img = firstItemImage(sel);
  const shipped = sel.status === "shipped";

  return (
    <Card pad={0}>
      <div
        style={{
          padding: "18px 20px",
          borderBottom: "1px solid rgba(196,193,183,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Eyebrow>Tracking</Eyebrow>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
            <span className="mono" style={{ fontSize: 13, fontWeight: 800 }}>
              {sel.orders?.order_number || sel.id.slice(0, 8)}
            </span>
            <span
              style={{
                padding: "3px 8px",
                borderRadius: 6,
                background: tone.bg,
                color: tone.fg,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: 0.5,
              }}
            >
              {map.label.toUpperCase()}
            </span>
          </div>
        </div>
        <Btn
          kind="ghost"
          size="sm"
          icon="message-square"
          onClick={() => {
            if (!sel.seller_id) return;
            onMessageSeller?.(sel.seller_id, {
              sub_order_id: sel.id,
              sub_number: sel.sub_number,
              parent_order_number: sel.orders?.order_number,
              image: img,
              status: sel.status,
            });
          }}
        >
          Message
        </Btn>
      </div>

      <div
        style={{
          padding: 20,
          display: "flex",
          justifyContent: "center",
          background: C.cream,
          borderBottom: "1px solid rgba(196,193,183,0.25)",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 180,
            height: 180,
            borderRadius: 16,
            background: img
              ? `url(${img}) center/cover no-repeat`
              : `linear-gradient(135deg, ${g1}, ${g2})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
          }}
        >
          {!img && <div style={{ fontSize: 56, filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.25))" }}>📦</div>}
          {shipped && (
            <div
              style={{
                position: "absolute",
                bottom: 10,
                left: 10,
                padding: "5px 9px",
                borderRadius: 10,
                background: "rgba(255,255,255,0.95)",
                fontSize: 10.5,
                fontWeight: 800,
                color: C.hunter,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: C.red }} />
              On the way
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: 20 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {STEPS.map((s, i) => {
            const done = i <= map.progress;
            const curr = i === map.progress && !["delivered", "completed"].includes(sel.status);
            return (
              <div key={s} style={{ display: "flex", gap: 14, alignItems: "flex-start", position: "relative" }}>
                {i < STEPS.length - 1 && (
                  <div
                    style={{
                      position: "absolute",
                      left: 14,
                      top: 30,
                      bottom: -14,
                      width: 2,
                      background: i < map.progress ? C.hunter : C.dustDk,
                    }}
                  />
                )}
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: done ? C.hunter : C.white,
                    border: done ? "none" : `2px solid ${C.dustDk}`,
                    color: done ? C.dust : C.muted,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                    flexShrink: 0,
                  }}
                >
                  {done ? (
                    <Icon name="check" size={14} color={C.dust} strokeWidth={3} />
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 800 }}>{i + 1}</span>
                  )}
                </div>
                <div style={{ flex: 1, paddingTop: 5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: done ? C.charcoal : C.slate }}>{s}</span>
                    {curr && (
                      <span
                        className="pulse"
                        style={{
                          padding: "1px 6px",
                          borderRadius: 4,
                          background: C.hunter,
                          color: C.dust,
                          fontSize: 9,
                          fontWeight: 800,
                          letterSpacing: 0.5,
                        }}
                      >
                        NOW
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                    {done && !curr ? fmtWhen(sel.updated_at) : curr ? "In progress" : "Waiting"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ height: 1, background: C.cream, margin: "18px 0 16px" }} />

        <Eyebrow>Items</Eyebrow>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          {(sel.order_items || []).map((it) => {
            const raw = it.products?.images;
            const arr = Array.isArray(raw) ? raw : [];
            const first = arr[0];
            const thumb = typeof first === "string" ? first : first?.thumb || first?.med || first?.full || it.product_image || null;
            return (
              <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    background: thumb ? `url(${thumb}) center/cover` : C.cream,
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: C.charcoal,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {itemTitle(it)}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    Qty {it.quantity}
                    {it.size ? ` · Size ${it.size}` : ""}
                  </div>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: C.charcoal }}>
                  {fmtK((it.unit_price || 0) * (it.quantity || 1))}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ height: 1, background: C.cream, margin: "16px 0" }} />

        <Eyebrow>Delivery</Eyebrow>
        <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: C.cream,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name={sel.delivery_method === "pickup" ? "store" : "truck"} size={15} color={C.hunter} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: C.charcoal }}>
              {DELIVERY_LABEL[sel.delivery_method] || sel.delivery_method || "—"}
            </div>
            {sel.delivery_method === "courier" && sel.delivery_address && (
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
                {sel.delivery_address}
              </div>
            )}
            {sel.delivery_method === "pickup" && (
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                Collect from {sel.sellers?.store_name || "the seller"}
              </div>
            )}
          </div>
        </div>

        <div style={{ height: 1, background: C.cream, margin: "14px 0" }} />

        <Eyebrow>Payment</Eyebrow>
        <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center" }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: C.cream,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name={PAYMENT_ICON[sel.payment_method] || "credit-card"} size={15} color={C.hunter} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: C.charcoal }}>
              {PAYMENT_LABEL[sel.payment_method] || "—"}
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
              {fmtK(sel.total)} · Paid on checkout
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: C.cream, margin: "16px 0" }} />

        <div style={{ display: "grid", gap: 4, fontSize: 12.5 }}>
          <RowLine label="Subtotal" value={fmtK(sel.subtotal)} />
          <RowLine label="Delivery" value={sel.delivery_fee > 0 ? fmtK(sel.delivery_fee) : "Free"} />
          <RowLine label="Total" value={fmtK(sel.total)} bold />
        </div>

        {sel.status === "delivered" && (
          <div
            style={{
              marginTop: 16,
              padding: 14,
              borderRadius: 14,
              background: C.greenBg,
              border: `1px solid rgba(58,90,64,0.15)`,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: C.hunter,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name="check" size={14} color={C.dust} strokeWidth={3} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.hunterDk }}>
                  Order delivered
                </div>
                <div style={{ fontSize: 11.5, color: C.slate, marginTop: 3, lineHeight: 1.45 }}>
                  Confirm receipt to release payment to the seller. Payment auto-releases in 48 hours.
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Btn full icon="check" onClick={() => setConfirmOpen(true)}>
                Confirm Received
              </Btn>
            </div>
          </div>
        )}

        {sel.status === "completed" && (
          <div style={{ marginTop: 14 }}>
            <Btn full icon="star" onClick={() => setReviewOpen(true)}>
              Leave review
            </Btn>
          </div>
        )}
      </div>
      {reviewOpen && <ReviewModal sel={sel} user={user} onClose={() => setReviewOpen(false)} />}
      {confirmOpen && (
        <ConfirmReceiptModal
          sel={sel}
          onClose={() => setConfirmOpen(false)}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ["buyer-orders", user?.id] });
          }}
        />
      )}
    </Card>
  );
}

function RowLine({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: C.muted, fontWeight: bold ? 800 : 600 }}>{label}</span>
      <span style={{ color: C.charcoal, fontWeight: bold ? 900 : 700, fontSize: bold ? 14 : 12.5 }}>{value}</span>
    </div>
  );
}
